import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { TenantOrganisation } from 'src/features/admin/types';
import useAuth from 'src/hooks/useAuth';
import { toTenantPath } from 'src/lib/tenantRouting';

export type TenantSettingsTab = 'organisation' | 'members';

type OpenTenantSettingsOptions = {
  tenant: TenantOrganisation;
  initialTabOnOpen?: TenantSettingsTab;
  openInviteModalOnOpen?: boolean;
  expandRequestsOnOpen?: boolean;
};

type TenantSettingsModalContextValue = {
  openTenantSettings: (options: OpenTenantSettingsOptions) => void;
  closeTenantSettings: () => void;
};

const TenantSettingsModalContext = createContext<TenantSettingsModalContextValue>({
  openTenantSettings: () => {},
  closeTenantSettings: () => {}
});

function settingsPathForTab(tab: TenantSettingsTab | string | null) {
  if (tab === 'members') return '/settings/members';
  return '/settings/organisation';
}

export function TenantSettingsModalProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const openTenantSettings = useCallback(
    (options: OpenTenantSettingsOptions) => {
      const params = new URLSearchParams();
      if (options.openInviteModalOnOpen) params.set('invite', '1');
      if (options.expandRequestsOnOpen) params.set('requests', '1');
      const path = settingsPathForTab(options.initialTabOnOpen ?? 'organisation');
      const query = params.toString();
      navigate(`${toTenantPath(path, options.tenant.code)}${query ? `?${query}` : ''}`);
    },
    [navigate]
  );

  const closeTenantSettings = useCallback(() => {}, []);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const tab = params.get('openSettings');
    if (!tab) return;

    const activeTenant = user.getActiveTenant(location.pathname);
    if (!activeTenant) return;

    params.delete('openSettings');
    const remaining = params.toString();
    navigate(`${toTenantPath(settingsPathForTab(tab), activeTenant.code)}${remaining ? `?${remaining}` : ''}`, {
      replace: true
    });
  }, [location.pathname, location.search, navigate, user]);

  const value = useMemo(
    () => ({
      openTenantSettings,
      closeTenantSettings
    }),
    [openTenantSettings, closeTenantSettings]
  );

  return <TenantSettingsModalContext.Provider value={value}>{children}</TenantSettingsModalContext.Provider>;
}

export function useTenantSettingsModal() {
  return useContext(TenantSettingsModalContext);
}
